import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import { BASE_URL } from "../../utils/constants";

const ActivityMappingForm = ({ initialData, onClose, onSuccess }) => {
  const [hotels, setHotels] = useState([]);
  const [allRoomTypes, setAllRoomTypes] = useState([]);
  const [allActivities, setAllActivities] = useState([]);
  const [filteredRoomTypes, setFilteredRoomTypes] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);

  const safeInitialData = initialData || {};

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      id: safeInitialData.id || null,
      hotel_id: safeInitialData.hotel_id || "",
      room_type_id: safeInitialData.room_type_id || "",
      activity_id: safeInitialData.activity_id || "",
      status: safeInitialData.status || "1",
    },
    shouldUnregister: false,
  });

  const hotel_id = watch("hotel_id");

  // Fetch hotels
  useEffect(() => {
    axios
      .get(`${BASE_URL}Hotel`)
      .then((res) => {
        if (res.data.success) {
          setHotels(res.data.data || []);
        }
      })
      .catch((err) => console.error("Error fetching hotels:", err));
  }, []);

  // Fetch room types & activities
  useEffect(() => {
    axios
      .get(`${BASE_URL}HotelRoom`)
      .then((res) => {
        if (res.data.success) {
          setAllRoomTypes(res.data.data || []);
        }
      })
      .catch((err) => console.error("Error fetching room types:", err));

    axios
      .get(`${BASE_URL}HmActivity`)
      .then((res) => {
        if (res.data.success) {
          setAllActivities(res.data.data || []);
        }
      })
      .catch((err) => console.error("Error fetching activities:", err));
  }, []);

  // Filter room types and activities based on hotel_id
  useEffect(() => {
    if (hotel_id) {
      const hotelIdNum = parseInt(hotel_id);

      const filteredRooms = allRoomTypes.filter(
        (room) => room.hotel_id === hotelIdNum,
      );
      setFilteredRoomTypes(filteredRooms);

      const filteredActs = allActivities.filter(
        (activity) => activity.hotel_id === hotelIdNum,
      );
      setFilteredActivities(filteredActs);

      // Only reset if it's not edit mode
      if (!safeInitialData?.id) {
        setValue("room_type_id", "");
        setValue("activity_id", "");
      }
    } else {
      setFilteredRoomTypes([]);
      setFilteredActivities([]);
    }
  }, [hotel_id, allRoomTypes, allActivities, setValue, safeInitialData?.id]);

  const onSubmit = async (formData) => {
    try {
      await onSuccess(formData);
    } catch (err) {
      console.error("Form submit error:", err);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ p: 3, borderRadius: 2, bgcolor: "#f9f9f9" }}
    >
      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: "bold", color: "#1a237e" }}
      >
        {safeInitialData?.id ? "Edit Activity Mapping" : "New Activity Mapping"}
      </Typography>

      <Grid container spacing={2}>
        {/* Hotel */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="hotel_id"
            control={control}
            rules={{ required: "Hotel is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Hotel"
                select
                fullWidth
                size="small"
                error={!!errors.hotel_id}
                helperText={errors.hotel_id?.message}
              >
                <MenuItem value="">Select Hotel</MenuItem>
                {hotels
                  .filter((hotel) => hotel.is_active === true)
                  .map((h) => (
                    <MenuItem key={h.hotel_id} value={h.hotel_id}>
                      {h.name}
                    </MenuItem>
                  ))}
              </TextField>
            )}
          />
        </Grid>

        {/* Room Type */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="room_type_id"
            control={control}
            rules={{ required: "Room Type is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Room Type"
                select
                fullWidth
                size="small"
                error={!!errors.room_type_id}
                helperText={errors.room_type_id?.message}
                disabled={!hotel_id}
              >
                <MenuItem value="">Select Room Type</MenuItem>
                {filteredRoomTypes.map((room) => (
                  <MenuItem key={room.room_id} value={room.room_id}>
                    {room.room_type}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        {/* Activity */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="activity_id"
            control={control}
            rules={{ required: "Activity is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Activity"
                select
                fullWidth
                size="small"
                error={!!errors.activity_id}
                helperText={errors.activity_id?.message}
                disabled={!hotel_id}
              >
                <MenuItem value="">Select Activity</MenuItem>
                {filteredActivities.map((activity) => (
                  <MenuItem
                    key={activity.activity_id}
                    value={activity.activity_id}
                  >
                    {activity.activity_name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        {/* Status */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="status"
            control={control}
            rules={{ required: "Status is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Status"
                select
                fullWidth
                size="small"
                error={!!errors.status}
                helperText={errors.status?.message}
              >
                <MenuItem value="">Select Status</MenuItem>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Inactive</MenuItem>
              </TextField>
            )}
          />
        </Grid>
      </Grid>

      {/* Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}>
        <Button onClick={onClose} variant="outlined" color="secondary">
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary">
          {safeInitialData?.id ? "Update" : "Save"}
        </Button>
      </Box>
    </Box>
  );
};

export default ActivityMappingForm;
